import { CSSProperties, forwardRef, useEffect, useState } from 'react'

import styles from './fadeInOut.module.scss'

type Status = 'hidden' | 'entering' | 'entered' | 'exiting'
type Props = {
  children: React.ReactNode
  show: boolean
  duration?: number
  inDuration?: number
  outDuration?: number
  unmountOnClosed?: boolean
  cssRemovedFromDOMOnClosed?: boolean
  className?: string
  style?: React.CSSProperties
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseDown?: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseUp?: (e: React.MouseEvent<HTMLDivElement>) => void
}

const HIDDEN: Status = 'hidden'
const ENTERING: Status = 'entering'
const ENTERED: Status = 'entered'
const EXITING: Status = 'exiting'

const STATE_CLASSES: Record<Status, string> = {
  hidden: styles.hidden,
  entering: styles.entering,
  entered: styles.entered,
  exiting: styles.exiting,
}

const FadeInOut = forwardRef((props: Props, ref: any) => {
  const {
    children,
    show,
    duration = 300,
    outDuration,
    inDuration,
    className = '',
    style,
    onClick,
    onMouseUp,
    onMouseDown,
    unmountOnClosed,
    cssRemovedFromDOMOnClosed,
  } = props

  const [status, statusClassName, extraStyles] = useTransition(show, { duration, inDuration, outDuration })

  if (unmountOnClosed && status === HIDDEN) return null
  if (cssRemovedFromDOMOnClosed && status === HIDDEN) extraStyles.display = 'none'

  return (
    <div
      ref={ref}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      className={`${className} ${statusClassName}`}
      style={{
        ...extraStyles,
        ...style,
      }}>
      {children}
    </div>
  )
})

export default FadeInOut

type TransitionOptions = {
  skipTransition?: boolean
  remainExiting?: boolean

  duration?: number
  inDuration?: number
  outDuration?: number

  enteringClassName?: string
  exitingClassName?: string
  hiddenClassName?: string
}

export const useTransition = (show: boolean, options?: TransitionOptions): [Status, string, CSSProperties] => {
  const [status, setStatus] = useState<Status>(show ? ENTERED : HIDDEN)

  const extraStyles: CSSProperties = {}

  if (options?.duration) extraStyles.animationDuration = `${options.duration}ms`
  if (options?.outDuration && status === EXITING) extraStyles.animationDuration = `${options.outDuration}ms`
  if (options?.inDuration && status === ENTERED) extraStyles.animationDuration = `${options.inDuration}ms`

  useEffect(() => {
    if (show) return performEnter()

    performExit()
  }, [show])

  const performEnter = () => {
    if (status === ENTERED) return
    if (options?.skipTransition) return setStatus(ENTERING)

    setStatus(ENTERING)
    setTimeout(() => setStatus(ENTERED), options?.inDuration ?? options?.duration ?? 300)
  }

  const performExit = () => {
    if (status === HIDDEN) return
    if (options?.skipTransition) return setStatus(HIDDEN)

    setStatus(EXITING)

    if (options?.remainExiting) return

    setTimeout(() => setStatus(HIDDEN), options?.outDuration ?? options?.duration ?? 300)
  }

  let className = STATE_CLASSES[status] || ''

  if (options?.hiddenClassName && status === HIDDEN) className = `${className} ${options.hiddenClassName}`
  if (options?.enteringClassName && status === ENTERING) className = `${className} ${options.enteringClassName}`
  if (options?.exitingClassName && status === EXITING) className = `${className} ${options.exitingClassName}`

  return [status, className, extraStyles]
}
